import { randomId } from "@/utils/randomId";

/**
 * Блокировка между вкладками одного origin. `isauthorized` ротирует refresh-cookie, поэтому два одновременных
 * запроса из разных вкладок разлогинили бы одну из них: каждый такой запрос идёт под этой блокировкой.
 *
 * Основной путь — `navigator.locks`. Его нет вне безопасного контекста (HTTP по IP, `docs/open_questions.md`,
 * «HTTPS»), тогда — аренда в `localStorage`: метка «занято вкладкой X до T» без токена. Если и `localStorage`
 * недоступен (приватный режим, запрет cookie), функция выполняется без блокировки.
 */

export const REFRESH_LOCK_NAME = "mado-auth-refresh";
/** Ключ аренды в `localStorage`. Значение — `{owner, until}`, токена в нём нет. */
export const REFRESH_LEASE_KEY = "mado-auth-refresh-lease";
/** Срок аренды: дольше таймаута запроса (30 с), чтобы её не перехватили во время `isauthorized`. */
export const REFRESH_LEASE_MS = 35_000;
/** Пауза после записи аренды: вкладка, записавшая позже, перезапишет метку, и первая это увидит. */
const LEASE_SETTLE_MS = 30;
const LEASE_POLL_MS = 100;

const tabId = randomId();
let pendingLock: Promise<unknown> = Promise.resolve();

interface Lease {
    owner: string;
    until: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function getStorage(): Storage | null {
    try {
        return typeof window === "undefined" ? null : window.localStorage;
    } catch {
        return null;
    }
}

function readLease(storage: Storage): Lease | null {
    try {
        const parsed: unknown = JSON.parse(storage.getItem(REFRESH_LEASE_KEY) ?? "null");
        if (typeof parsed !== "object" || parsed === null) return null;
        const { owner, until } = parsed as Record<string, unknown>;
        return typeof owner === "string" && typeof until === "number" ? { owner, until } : null;
    } catch {
        return null;
    }
}

function tryWriteLease(storage: Storage, lease: Lease): boolean {
    try {
        storage.setItem(REFRESH_LEASE_KEY, JSON.stringify(lease));
        return true;
    } catch {
        return false;
    }
}

async function withStorageLease<T>(storage: Storage, fn: () => Promise<T>): Promise<T> {
    for (;;) {
        const current = readLease(storage);
        if (!current || current.until <= Date.now() || current.owner === tabId) {
            // Хранилище переполнено или запрещено — блокировка невозможна, ждать её бессмысленно.
            if (!tryWriteLease(storage, { owner: tabId, until: Date.now() + REFRESH_LEASE_MS })) return fn();
            await sleep(LEASE_SETTLE_MS);
            if (readLease(storage)?.owner === tabId) break;
        }
        await sleep(LEASE_POLL_MS);
    }

    try {
        return await fn();
    } finally {
        if (readLease(storage)?.owner === tabId) storage.removeItem(REFRESH_LEASE_KEY);
    }
}

/**
 * Выполнить `fn` под блокировкой `REFRESH_LOCK_NAME` — не одновременно с другими вкладками.
 * Внутри вкладки также сериализует вход, регистрацию и refresh, включая запасной путь без navigator.locks.
 */
export function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    const result = pendingLock.then(() => acquireRefreshLock(fn));
    pendingLock = result.then(
        () => undefined,
        () => undefined,
    );
    return result;
}

function acquireRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
    if (typeof locks?.request === "function") {
        return locks.request(REFRESH_LOCK_NAME, () => fn()) as Promise<T>;
    }

    const storage = getStorage();
    return storage ? withStorageLease(storage, fn) : fn();
}
